/* ------------------------------------------------------------------------------
@name: FormatDate
--------------------------------------------------------------------------------- */

const FormatDate = (() => {
  // --- handle format custom date
  const handleFormatCustomDate = (_date) => {
    var dd = String(_date.getDate()).padStart(2, "0");
    var mm = String(_date.getMonth() + 1).padStart(2, "0");
    var yyyy = _date.getFullYear();
    _date = dd + "-" + mm + "-" + yyyy;

    return _date;
  };

  // --- handle split date range
  const handleSplitDateRange = (date) => {
    return date.replace(" - ", "-").split("-");
  };

  // --- handle format date renge
  const handleFormatRangeDate = (date) => {
    const _date = handleSplitDateRange(date);
    return {
      startDate: _date[0].replaceAll("/", "-"),
      endDate: _date[1].replaceAll("/", "-"),
    };
  };

  // --- handle format dash date
  const handleFormatDashDate = (date) => {
    return date.replaceAll("/", "-");
  };

  const handleFormatInboundDate = (timeStr = false, dateStr = false) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const clean = (v) =>
      v === null || v === undefined ? "" : String(v).trim();

    // normalize inputs (handle boolean false, null, undefined)
    const rawTime = timeStr === false ? "" : clean(timeStr);
    const rawDate = dateStr === false ? "" : clean(dateStr);

    // helpers
    const pad2 = (n) => String(n).padStart(2, "0");
    const fmtTimeFromDate = (d) =>
      `${pad2(d.getHours())}.${pad2(d.getMinutes())}`;
    const fmtDateFromDate = (d) => {
      const dd = pad2(d.getDate());
      const mon = months[d.getMonth()] || "";
      const yyyy = d.getFullYear();
      return `${dd} ${mon} ${yyyy}`;
    };

    // quick exit
    if (!rawTime && !rawDate) return "";

    // Try to parse an input into a Date instance
    const toDate = (timePart, datePart) => {
      // If already a Date object
      if (timePart instanceof Date && !datePart) return timePart;
      if (datePart instanceof Date && !timePart) return datePart;
      if (timePart instanceof Date && datePart instanceof Date) {
        // use datePart's date + timePart's time
        const dt = new Date(datePart.getTime());
        dt.setHours(
          timePart.getHours(),
          timePart.getMinutes(),
          timePart.getSeconds() || 0,
          0
        );
        return dt;
      }

      // If datePart looks like YYYY-MM-DD (or YYYY/MM/DD), build ISO with time
      if (datePart && /^\d{4}[-/]\d{2}[-/]\d{2}$/.test(datePart)) {
        let timeForParse = "00:00:00";
        if (timePart) {
          const tNorm = String(timePart).replace(/\./g, ":");
          const parts = tNorm.split(":");
          const hh = pad2(parts[0] || "0");
          const mm = pad2(parts[1] || "0");
          const ss = pad2(parts[2] || "0");
          timeForParse = `${hh}:${mm}:${ss}`;
        }
        const iso = `${datePart}T${timeForParse}`;
        const d = new Date(iso);
        if (!isNaN(d.getTime())) return d;
      }

      // Try combined string parsing (date + time or time alone)
      let combined = "";
      if (datePart && timePart) combined = `${datePart} ${timePart}`;
      else if (datePart) combined = datePart;
      else combined = timePart;

      // normalize time separators
      combined = combined.replace(/\./g, ":");

      // Try native parse
      let d = new Date(combined);
      if (!isNaN(d.getTime())) return d;

      // Try DD-MM-YYYY or DD/MM/YYYY with optional time
      const m = combined.match(
        /^(\d{2})[\/\-](\d{2})[\/\-](\d{4})(?:\s+(\d{1,2})(?::|\.)?(\d{2})?)?/
      );
      if (m) {
        const dd = m[1],
          mm = m[2],
          yy = m[3];
        const hh = m[4] ? pad2(m[4]) : "00";
        const mins = m[5] ? pad2(m[5]) : "00";
        d = new Date(`${yy}-${mm}-${dd}T${hh}:${mins}:00`);
        if (!isNaN(d.getTime())) return d;
      }

      // Try time-only HH:MM (use today's date)
      const tOnly = String(timePart || "").trim();
      if (tOnly && /^(\d{1,2})([:\.]\d{1,2})?$/.test(tOnly) && !datePart) {
        const tn = tOnly.replace(/\./g, ":");
        const parts = tn.split(":");
        const hh = Number(parts[0]) || 0;
        const mm = Number(parts[1]) || 0;
        const now = new Date();
        now.setHours(hh, mm, 0, 0);
        return now;
      }

      // give up
      return null;
    };

    // If both present try to parse combined and format both parts
    if (rawDate && rawTime) {
      const dt = toDate(rawTime, rawDate);
      if (dt) {
        return `${fmtTimeFromDate(dt)}, ${fmtDateFromDate(dt)}`;
      }
      // fallback: try parsing date separately and time separately
      const parsedDate = toDate(null, rawDate);
      const parsedTime = toDate(rawTime, null);
      if (parsedTime && parsedDate) {
        // combine parsedDate date with parsedTime time
        const combined = new Date(parsedDate.getTime());
        combined.setHours(
          parsedTime.getHours(),
          parsedTime.getMinutes(),
          parsedTime.getSeconds() || 0,
          0
        );
        return `${fmtTimeFromDate(combined)}, ${fmtDateFromDate(combined)}`;
      }
      // last resort return raw concatenation
      return `${rawTime}, ${rawDate}`;
    }

    // Only time present
    if (rawTime && !rawDate) {
      // parse time and output hh.mm
      const t = toDate(rawTime, null);
      if (t) return fmtTimeFromDate(t);
      // fallback normalize hh.mm or hh:mm string
      const tNorm = rawTime.replace(/\./g, ":");
      const parts = tNorm.split(":");
      const hh = pad2(parts[0] || "0");
      const mm = pad2(parts[1] || "0");
      return `${hh}.${mm}`;
    }

    // Only date present
    if (!rawTime && rawDate) {
      const d =
        toDate(null, rawDate) || toDate(null, rawDate.replace(/-/g, "/"));
      if (d) return fmtDateFromDate(d);
      // fallback raw
      return rawDate;
    }

    return "";
  };

  return {
    custom: handleFormatCustomDate,
    range: handleFormatRangeDate,
    dash: handleFormatDashDate,
    inboundFormat: handleFormatInboundDate,
  };
})();

export default FormatDate;
